import { useLanguage } from "@/contexts/LanguageContext";
import { SEO } from "@/components/SEO";
import { Link } from "react-router-dom";

import { HelpCircle, MessageCircle, ArrowRight } from "lucide-react";
import { PageHero } from "@/components/shared/PageHero";
import { GlowCard } from "@/components/shared/GlowCard";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";

const FAQs = () => {
  const { language } = useLanguage();
  const { ref } = useRevealOnScroll<HTMLDivElement>();

  const faqs =
    language === "ar"
      ? [
          { question: "ما هي فيوجن ميديا؟", answer: "فيوجن ميديا هي شركة رائدة في مجال الإعلانات الرقمية الخارجية في المملكة العربية السعودية، نقدم حلول إعلانية مبتكرة باستخدام التكنولوجيا البرمجية." },
          { question: "كيف يمكنني بدء حملة إعلانية؟", answer: "يمكنك بدء حملتك الإعلانية بسهولة من خلال النقر على زر 'إنشاء حملة' في القائمة الرئيسية. سيوجهك النظام خلال عملية إنشاء الحملة خطوة بخطوة." },
          { question: "ما هي طرق الدفع المتاحة؟", answer: "نقبل مجموعة متنوعة من طرق الدفع تشمل البطاقات الائتمانية وآبل باي، كما نوفر خيار التحويل البنكي مع فترة سداد تصل إلى 60 يوماً." },
          { question: "هل يمكنني تعديل حملتي بعد إطلاقها؟", answer: "نعم، يمكنك تعديل حملتك في أي وقت من خلال التواصل مع فريق خدمة العملاء لدينا." },
        ]
      : [
          { question: "What is Fusion Media?", answer: "Fusion Media is a leading digital out-of-home advertising company in Saudi Arabia, offering innovative advertising solutions using programmatic technology." },
          { question: "How can I start an advertising campaign?", answer: "You can easily start your advertising campaign by clicking the 'Create Campaign' button in the main menu. The system will guide you through the campaign creation process step by step." },
          { question: "What payment methods are available?", answer: "We accept a variety of payment methods including credit cards and Apple Pay, and we also offer bank transfer options with payment terms up to 60 days." },
          { question: "Can I modify my campaign after launch?", answer: "Yes, you can modify your campaign at any time by contacting our customer service team." },
        ];

  return (
    <div className="relative min-h-screen">
      <SEO
        title="FAQs — Fusion Media Advertising Platform"
        description="Answers to common questions about Fusion Media campaigns, payment methods, and the platform."
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.question,
            acceptedAnswer: { "@type": "Answer", text: f.answer },
          })),
        }}
      />
      <div className="relative max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8">

        <PageHero
          eyebrow="Help & Support"
          title={language === "ar" ? "الأسئلة" : "Frequently asked"}
          accent={language === "ar" ? "الشائعة" : "questions"}
          subtitle={
            language === "ar"
              ? "اعثر على إجابات لأكثر الأسئلة شيوعاً حول خدماتنا وحلولنا الإعلانية المتقدمة"
              : "Find answers to the most common questions about our services and innovative advertising solutions."
          }
          centered
        />

        <section ref={ref} className="reveal-on-scroll pb-16 md:pb-24">
          <div className="max-w-4xl mx-auto grid gap-5">
            {faqs.map((faq, i) => (
              <GlowCard key={i} icon={HelpCircle} accent="blue" title={faq.question} description={faq.answer} />
            ))}
          </div>
        </section>

        <section className="pb-16 md:pb-24">
          <div className="relative rounded-3xl overflow-hidden p-10 md:p-16 text-center bg-gradient-to-br from-primary via-primary-light to-primary shadow-2xl shadow-primary/40">
            <div aria-hidden className="absolute inset-0 pointer-events-none">
              <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full bg-white/15 blur-3xl" />
              <div className="absolute -bottom-24 -left-24 w-80 h-80 rounded-full bg-white/15 blur-3xl" />
            </div>
            <div className="relative">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/15 border border-white/30 text-white text-[11px] font-semibold tracking-widest uppercase mb-6 backdrop-blur">
                <MessageCircle className="w-3.5 h-3.5" />
                Still have questions?
              </div>
              <h2 className="text-3xl md:text-5xl font-bold text-white tracking-tight mb-4">
                {language === "ar" ? "لم تجد ما تبحث عنه؟" : "Didn't find what you're looking for?"}
              </h2>
              <p className="text-base md:text-xl text-white/85 max-w-2xl mx-auto mb-8">
                {language === "ar"
                  ? "فريقنا المتخصص متاح للإجابة على جميع استفساراتك"
                  : "Our specialized team is available to answer all your questions."}
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 h-14 px-9 rounded-full bg-white text-primary font-bold text-base shadow-2xl hover:-translate-y-1 transition-transform duration-300 group"
              >
                {language === "ar" ? "تواصل معنا" : "Contact Us"}
                <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-0.5" />
              </Link>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default FAQs;
